import {
  FormTemplateAttribute,
  FormTemplateFieldConditionalRule,
} from 'mapistry-shared';
import { useMemo } from 'react';
import { useForm } from 'react-final-form';
import { FieldConditionHelpers } from '../../hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from '../../types';

function canBeConditionallyShown(field: EditableFormTemplateField) {
  const isInspectionDate = field.attributes?.includes(
    FormTemplateAttribute.IS_INSPECTION_DATE,
  );
  return !isInspectionDate;
}

type UseGetAvailableAndUnavailableFieldsProps = {
  fieldsInGroup: EditableFormTemplateField[];
  fieldConditionHelpers: FieldConditionHelpers;
  triggerFieldId: string;
};

export function useGetAvailableAndUnavailableFields({
  fieldsInGroup,
  fieldConditionHelpers,
  triggerFieldId,
}: UseGetAvailableAndUnavailableFieldsProps): {
  unavailableFields: EditableFormTemplateField[];
  availableFields: EditableFormTemplateField[];
} {
  const form = useForm<{
    conditionalRules: FormTemplateFieldConditionalRule[];
  }>();
  const {
    values: { conditionalRules: conditionalRulesFromFormState },
  } = form.getState();

  return useMemo(
    () =>
      fieldsInGroup.reduce(
        (acc, field) => {
          const isSelf = field.id === triggerFieldId;
          if (isSelf || !canBeConditionallyShown(field)) {
            return acc;
          }

          const triggerField =
            fieldConditionHelpers.getTriggerFieldByConditionallyShownFieldId(
              field.id,
            );

          // for different trigger fields, look at higher up state
          const isConditionallyShownBasedOnADifferentField =
            triggerField && triggerField.id !== triggerFieldId;

          // for the current trigger field, look at the active (unsubmitted) form state
          const isConditionallyShownBasedOnThisField =
            conditionalRulesFromFormState?.some((rule) =>
              rule.conditionallyShownFields.some((f) => f.id === field.id),
            );

          const isConditionallyShown =
            isConditionallyShownBasedOnADifferentField ||
            isConditionallyShownBasedOnThisField;

          if (isConditionallyShown) {
            acc.unavailableFields.push(field);
          } else {
            acc.availableFields.push(field);
          }
          return acc;
        },
        {
          unavailableFields: [] as EditableFormTemplateField[],
          availableFields: [] as EditableFormTemplateField[],
        },
      ),
    [
      conditionalRulesFromFormState,
      fieldConditionHelpers,
      triggerFieldId,
      fieldsInGroup,
    ],
  );
}
